import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';

interface Student {
  id: string;
  name: string;
  email: string;
  phone: string;
  class?: string;
  batchId: string;
  batchName: string;
  joinDate: string;
  status: 'active' | 'inactive' | 'completed' | 'confirmed';
  createdAt?: any; // Firestore Timestamp or Date
  paymentPaid?: Record<string, boolean>; // month -> paid
  phoneNumber?: string; // some docs may use phoneNumber key
}


const StudentManagement = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [batches, setBatches] = useState<{id: string, name: string, class?: string}[]>([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<{ name: string; email: string; phone: string; batchId: string }>({
    name: '',
    email: '',
    phone: '',
    batchId: ''
  });

  useEffect(() => {
    fetchBatches();
    fetchStudents();
  }, []);

  const fetchBatches = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'batches'));
      const batchesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        class: doc.data().class,
      }));
      setBatches(batchesData);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      // Fetch students from 'users' collection where role is 'student'
      const studentsQuery = query(collection(db, 'users'));
      const querySnapshot = await getDocs(studentsQuery);
      const studentsData = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter((user: any) => user.role === 'student') as Student[];
      setStudents(studentsData);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const approveStudent = async (studentId: string) => {
    const confirm = window.confirm('Are you sure you want to approve this student?');
    if (!confirm) return;
    try {
      const userRef = doc(db, 'users', studentId);
      await updateDoc(userRef, { status: 'confirmed' });
      fetchStudents();
    } catch (error) {
      alert('Failed to approve student.');
      console.error('Error approving student:', error);
    }
  };

  const deleteStudent = async (studentId: string) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this student? This action cannot be undone.');
    if (!confirmDelete) return;
    try {
      await deleteDoc(doc(db, 'users', studentId));
      fetchStudents();
    } catch (error) {
      alert('Failed to delete student.');
      console.error('Error deleting student:', error);
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBatch = selectedBatch === 'all' || student.batchId === selectedBatch;
    return matchesSearch && matchesBatch;
  });

  // Categorize students
  const pendingStudents = filteredStudents.filter(s => s.status !== 'confirmed');
  const approvedStudents = filteredStudents.filter(s => s.status === 'confirmed');
  const sortedStudents = [...pendingStudents, ...approvedStudents];

  // Group approved students by class
  const approvedByClass = approvedStudents.reduce<Record<string, Student[]>>((acc, student) => {
    const cls = student.class || 'Unknown';
    if (!acc[cls]) acc[cls] = [];
    acc[cls].push(student);
    return acc;
  }, {});

  const getCurrentMonthName = () => {
    const monthNames = [
      'January','February','March','April','May','June','July','August','September','October','November','December'
    ];
    return monthNames[new Date().getMonth()];
  };

  const getPaidStatusText = (student?: Student | null) => {
    if (!student) return 'Unknown';
    const payment = student.paymentPaid || {};
    const month = getCurrentMonthName();
    const isPaid = Boolean((payment as Record<string, boolean>)[month]);
    return isPaid ? 'Paid' : 'Unpaid';
  };

  const openDetails = (student: Student) => {
    setSelectedStudent(student);
    setShowDetailsModal(true);
  };

  const closeDetails = () => {
    setShowDetailsModal(false);
    setSelectedStudent(null);
  };

  const openEdit = (student: Student) => {
    setSelectedStudent(student);
    setEditForm({
      name: student.name || '',
      email: student.email || '',
      phone: student.phone || student.phoneNumber || '',
      batchId: student.batchId || ''
    });
    setShowEditModal(true);
  };

  const closeEdit = () => {
    setShowEditModal(false);
    setSelectedStudent(null);
  };

  const saveEdit = async () => {
    if (!selectedStudent) return;
    try {
      const batchName = (batches as any[])
        .filter((b: any) => b.id === editForm.batchId)
        .map((b: any) => b.name)[0] || selectedStudent.batchName || '';
      const userRef = doc(db, 'users', selectedStudent.id);
      await updateDoc(userRef, {
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone,
        phoneNumber: editForm.phone, // keep both keys in sync
        batchId: editForm.batchId,
        batchName: batchName,
      });
      await fetchStudents();
      setShowEditModal(false);
      setSelectedStudent(null);
    } catch (error) {
      alert('Failed to update student.');
      console.error('Error updating student:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-blue-100 text-blue-800'
    };
    
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusClasses[status as keyof typeof statusClasses] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Student Management</h2>
        <div className="flex space-x-4">
          <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md">
            Export Data
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              id="search"
              placeholder="Search by name or email"
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="batch" className="block text-sm font-medium text-gray-700 mb-1">Filter by Batch</label>
            <select
              id="batch"
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              value={selectedBatch}
              onChange={(e) => setSelectedBatch(e.target.value)}
            >
              <option value="all">All Batches</option>
              {batches.map(batch => (
                <option key={batch.id} value={batch.id}>{batch.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button 
              className="bg-white border border-gray-300 rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => {
                setSearchTerm('');
                setSelectedBatch('all');
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Pending Students Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-10">
        <h3 className="text-lg font-bold px-6 pt-6 pb-2 text-yellow-700">Pending Students</h3>
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading students...</p>
          </div>
        ) : pendingStudents.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-600">No pending students found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Join Date</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pendingStudents.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-blue-600 font-medium">
                            {(student.name ? student.name.charAt(0).toUpperCase() : '?')}
                          </span>
                        </div>
                        <div className="ml-4">
                          <button
                            type="button"
                            className="text-left text-sm font-medium text-blue-700 hover:underline"
                            onClick={() => openDetails(student)}
                          >
                            {student.name || 'N/A'}
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{student.phone || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{student.class || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{student.batchName || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {student.createdAt ? new Date(student.createdAt.seconds ? student.createdAt.seconds * 1000 : student.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(student.status || 'inactive')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        onClick={() => approveStudent(student.id)}
                        disabled={student.status === 'confirmed'}
                      >
                        {student.status === 'confirmed' ? 'Approved' : 'Approve'}
                      </button>
                      <button
                        className="text-red-600 hover:text-red-800"
                        onClick={() => deleteStudent(student.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Approved Students by Class */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <h3 className="text-lg font-bold px-6 pt-6 pb-2 text-green-700">Approved Students</h3>
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading students...</p>
          </div>
        ) : approvedStudents.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-600">No approved students found.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.keys(approvedByClass).sort().map((cls) => (
              <div key={cls} className="overflow-x-auto">
                <div className="px-6 pt-4">
                  <h4 className="text-md font-semibold text-gray-800">Class: {cls}</h4>
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Join Date</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {approvedByClass[cls].map((student) => (
                      <tr key={student.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-blue-600 font-medium">
                                {(student.name ? student.name.charAt(0).toUpperCase() : '?')}
                              </span>
                            </div>
                            <div className="ml-4">
                              <button
                                type="button"
                                className="text-left text-sm font-medium text-blue-700 hover:underline"
                                onClick={() => openDetails(student)}
                              >
                                {student.name || 'N/A'}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{student.phone || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{student.class || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{student.batchName || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {student.createdAt ? new Date(student.createdAt.seconds ? student.createdAt.seconds * 1000 : student.createdAt).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(student.status || 'inactive')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            className="text-blue-600 hover:text-blue-900 mr-3"
                            onClick={() => approveStudent(student.id)}
                            disabled={student.status === 'confirmed'}
                          >
                            {student.status === 'confirmed' ? 'Approved' : 'Approve'}
                          </button>
                          <button
                            className="text-indigo-600 hover:text-indigo-900"
                            onClick={() => openEdit(student)}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-40"
            onClick={closeDetails}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6 z-10">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-xl font-semibold text-gray-900">Student Details</h4>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={closeDetails}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Name</span><span className="font-medium text-gray-900">{selectedStudent.name || 'N/A'}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Email</span><span className="font-medium text-gray-900">{selectedStudent.email || 'N/A'}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Phone</span><span className="font-medium text-gray-900">{selectedStudent.phone || selectedStudent.phoneNumber || 'N/A'}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Batch</span><span className="font-medium text-gray-900">{selectedStudent.batchName || 'N/A'}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Payment ({getCurrentMonthName()})</span>
                <span className={`font-semibold ${getPaidStatusText(selectedStudent) === 'Paid' ? 'text-green-700' : 'text-red-700'}`}>
                  {getPaidStatusText(selectedStudent)}
                </span>
              </div>
            </div>
            <div className="mt-6 flex justify-between">
              <button
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={closeDetails}
              >
                Close
              </button>
              <button
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
                onClick={async () => {
                  await deleteStudent(selectedStudent.id);
                  closeDetails();
                }}
              >
                Delete Student
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={closeEdit} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6 z-10">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-xl font-semibold text-gray-900">Edit Student</h4>
              <button className="text-gray-500 hover:text-gray-700" onClick={closeEdit} aria-label="Close">✕</button>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Name</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Phone Number</label>
                <input
                  type="tel"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Batch</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editForm.batchId}
                  onChange={(e) => setEditForm({ ...editForm, batchId: e.target.value })}
                >
                  <option value="">-- Select Batch --</option>
                  {(batches as any[])
                    .filter((b: any) => !selectedStudent.class || b.class === selectedStudent.class)
                    .map((b: any) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50" onClick={closeEdit}>Cancel</button>
              <button className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700" onClick={saveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {filteredStudents.length > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-b-lg">
          <div className="flex-1 flex justify-between sm:hidden">
            <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
              Previous
            </button>
            <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">1</span> to <span className="font-medium">10</span> of{' '}
                <span className="font-medium">{filteredStudents.length}</span> students
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
                  1
                </button>
                <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
                  2
                </button>
                <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentManagement;
